import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

function toMin(t) { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; }
function dayKey(dateStr) { return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }).toLowerCase(); }

const DEFAULT_COLORS = {
  confirmed: "#3B82F6", seated: "#3B82F6", completed: "#22C55E",
  no_show: "#EF4444", pending: "#F59E0B", walkin: "#D97706",
};
function blockColor(b, cc) {
  const occ = (b.occasion || "").toLowerCase();
  if (occ) {
    for (const rule of cc?.occasions || []) {
      if (rule.match && occ.includes(rule.match.toLowerCase())) return rule.color;
    }
  }
  const st = cc?.statuses || {};
  if (b.source === "walkin") return st.walkin || DEFAULT_COLORS.walkin;
  return st[b.status] || DEFAULT_COLORS[b.status] || DEFAULT_COLORS.confirmed;
}

export default function GuestListPrint() {
  const { rid, date } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/restaurants/${rid}`),
      api.get(`/restaurants/${rid}/bookings`, { params: { date } }),
    ]).then(([r, b]) => { setRestaurant(r.data); setBookings(b.data); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load the guest list."));
  }, [rid, date]);

  useEffect(() => {
    if (restaurant && bookings) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [restaurant, bookings]);

  const groups = useMemo(() => {
    if (!restaurant || !bookings) return [];
    const wd = dayKey(date);
    const svcs = (restaurant.services || []).filter((s) => s.enabled !== false && s.open && s.close &&
      (!s.days?.length || s.days.some((d) => String(d).slice(0, 3).toLowerCase() === wd)));
    const active = bookings.filter((b) => !["cancelled", "no_show"].includes(b.status)).sort((a, b) => toMin(a.time) - toMin(b.time));
    if (!svcs.length) return [{ name: "All bookings", items: active }];
    const gs = svcs.map((s) => ({
      name: `${s.name} · ${s.open}–${s.close}`,
      items: active.filter((b) => toMin(b.time) >= toMin(s.open) && toMin(b.time) < toMin(s.close)),
    }));
    const used = new Set(gs.flatMap((g) => g.items.map((b) => b.id)));
    const rest = active.filter((b) => !used.has(b.id));
    if (rest.length) gs.push({ name: "Other times", items: rest });
    return gs;
  }, [restaurant, bookings, date]);

  const inactive = useMemo(() => (bookings || [])
    .filter((b) => ["cancelled", "no_show"].includes(b.status))
    .sort((a, b) => toMin(a.time) - toMin(b.time)), [bookings]);

  if (err) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-8" data-testid="guest-list-error">
        <p>{err}</p>
      </div>
    );
  }
  if (!restaurant || !bookings) return <div className="min-h-screen bg-white" />;

  const totalBookings = groups.flatMap((g) => g.items).length;
  const totalCovers = groups.flatMap((g) => g.items).reduce((n, b) => n + (b.party_size || 0), 0);
  const niceDate = new Date(date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const renderRow = (b, grey) => (
    <tr key={b.id} data-testid={`print-booking-${b.id}`} className={`border-b border-black/10 align-top ${grey ? "opacity-50" : ""}`}>
      <td className="py-2 pr-3 font-medium whitespace-nowrap">
        <span data-testid={`print-dot-${b.id}`} style={{ backgroundColor: blockColor(b, restaurant?.calendar_colors) }}
          className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" />
        {b.time}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">{b.table_label || "—"}</td>
      <td className="py-2 pr-3">
        <p className="font-medium">{b.name}</p>
        {b.phone && <p className="text-xs text-black/60">{b.phone}</p>}
      </td>
      <td className="py-2 pr-3 text-center">{b.party_size}</td>
      <td className="py-2 pr-3 text-xs">
        {b.occasion && <p>Occasion: {b.occasion}</p>}
        {b.dietary && <p>Dietary: {b.dietary}</p>}
        {b.notes && <p>Notes: {b.notes}</p>}
      </td>
      <td className="py-2 text-xs capitalize">{b.status.replace("_", " ")}</td>
    </tr>
  );

  return (
    <div data-testid="guest-list-print" className="min-h-screen bg-white text-black">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-black/50">Guest list</p>
            <h1 className="text-3xl font-semibold mt-1">{restaurant.name}</h1>
            <p data-testid="print-date" className="text-black/60 mt-1">{niceDate}</p>
          </div>
          <Button data-testid="print-now-btn" onClick={() => window.print()} className="no-print rounded-full">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
        <p data-testid="print-summary" className="mt-4 text-sm text-black/60">
          {totalBookings} booking{totalBookings === 1 ? "" : "s"} · {totalCovers} covers
        </p>
        {groups.map((g, gi) => (
          <div key={g.name} data-testid={`print-service-${gi}`} className="mt-8">
            <h2 className="text-sm uppercase tracking-[0.2em] text-black/50 border-b border-black/20 pb-2">
              {g.name} <span className="normal-case tracking-normal">({g.items.length})</span>
            </h2>
            {g.items.length === 0 ? (
              <p className="text-sm text-black/40 py-3">No bookings.</p>
            ) : (
              <table className="w-full text-left text-sm mt-1">
                <thead>
                  <tr className="border-b border-black/20 text-xs uppercase tracking-wider text-black/50">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Table</th>
                    <th className="py-2 pr-3">Guest</th>
                    <th className="py-2 pr-3 text-center">Party</th>
                    <th className="py-2 pr-3">Details</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>{g.items.map((b) => renderRow(b, false))}</tbody>
              </table>
            )}
          </div>
        ))}
        {inactive.length > 0 && (
          <div data-testid="print-cancelled-section" className="mt-10">
            <h2 className="text-sm uppercase tracking-[0.2em] text-black/40 border-b border-black/10 pb-2">
              Cancelled / no-show ({inactive.length})
            </h2>
            <table className="w-full text-left text-sm mt-1">
              <tbody>{inactive.map((b) => renderRow(b, true))}</tbody>
            </table>
          </div>
        )}
        <p className="mt-10 text-xs text-black/40">Printed from TableDiary · {new Date().toLocaleString("en-GB")}</p>
      </div>
    </div>
  );
}
